/**
 * taxCalcSnapshotDiff — compare two save-history snapshots of ONE Tax Calculate V2 version.
 *
 * A snapshot's `payloadJson` is the whole formula set of one SAVE round, serialized straight off the
 * EF entities:  `{ functions: TaxCalcFunctionV2[] }` (camelCase keys, see
 * `Model/AppModel/TaxCalculateV2Models.cs`). The capture side has NO hand-written column list, so
 * this diff must not have one either: every key present on either side is compared, which is what
 * makes a column added to `tax.TaxCalcFunctionV2` later show up in the compare dialog on its own.
 *
 * Three ideas carry the whole file:
 *
 *   1. A formula's identity is its NATURAL key — `functionCode`, which IS the C# class name declared
 *      inside `sourceCode` and is unique per calc version. NEVER `functionID`: restore reconciles by
 *      id where it can, but an import/clone hands the same formula a different one, and an id-based
 *      match would then report a whole version as removed + added.
 *
 *   2. The audit/identity columns AND the whole compile cache are NOISE. `updatedDate` moves on every
 *      save; `returnType` / `parametersJson` / `sourceHash` / `compileStatus` / `compileErrorsJson` /
 *      `compiledDate` are overwritten by every compile — they follow `sourceCode`, they are not
 *      authored. Leaving any of them in would mark every formula "modified" every round and the diff
 *      would say nothing. They are stripped by name (case-insensitively) via IGNORED_FIELDS.
 *      `parameterBindingsJson` is deliberately NOT in that list: it is authored data (a binding is
 *      only ever rewritten by an explicit edit), so a change there is a real change.
 *
 *   3. `sourceCode` is a long multi-line blob. Line endings are not content: a file that travelled
 *      through a CRLF checkout must not read as "every formula edited", so newlines are normalized
 *      to `\n` before anything is compared.
 *
 * Pure logic — NO Vue/Vuetify import — so it runs under plain Node for the unit tests.
 *   Test:  cd WebAPI/ClientApp && node --test tests/taxCalcSnapshotDiff.test.mjs
 */

/**
 * Columns that must never make a formula read as "modified":
 *   · identity + FK   — `functionID`, `calcVersionID` (an id is per-database, not per-formula)
 *   · audit stamps    — `createdBy/Date`, `updatedBy/Date`
 *   · the EF nav prop — `taxCalcVersionV2`
 *   · the compile CACHE — `returnType`, `parametersJson`, `sourceHash`, `compileStatus`,
 *     `compileErrorsJson`, `compiledDate`. These are derived from `sourceCode`; they change whenever
 *     it does (and whenever someone merely re-compiles), so counting them would double-report every
 *     source edit and turn a "compile only" round into a full-board change.
 * Compared case-insensitively — the payload is written with a camelCase policy, but a payload
 * captured before that policy existed (or hand-edited) can still arrive PascalCase.
 */
export const IGNORED_FIELDS = Object.freeze([
  'functionID',
  'calcVersionID',
  'createdBy',
  'createdDate',
  'updatedBy',
  'updatedDate',
  'taxCalcVersionV2',
  // compile cache — follows sourceCode, never authored
  'returnType',
  'parametersJson',
  'sourceHash',
  'compileStatus',
  'compileErrorsJson',
  'compiledDate',
]);

const IGNORED = new Set(IGNORED_FIELDS.map((f) => f.toLowerCase()));

const EMPTY_PAYLOAD = () => ({ functions: [] });

/** Case-insensitive property read — exact hit first (the normal path), then a scan. */
function getProp(obj, name) {
  if (!obj) return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, name)) return obj[name];
  const lname = String(name).toLowerCase();
  const hit = Object.keys(obj).find((k) => k.toLowerCase() === lname);
  return hit === undefined ? undefined : obj[hit];
}

/** CRLF → LF. A line ending is not an edit; `sourceCode` is the reason this exists. */
function normalizeNewlines(s) {
  return s.indexOf('\r') === -1 ? s : s.replace(/\r\n/g, '\n');
}

/**
 * null/undefined → '' · object/array → JSON · everything else → String().
 * Newlines are normalized on both paths — inside a JSON blob a CRLF is escaped as `\r\n`, so the
 * escaped form is folded too, otherwise `parameterBindingsJson` would flip on line endings alone.
 */
function normalizeValue(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    try {
      return normalizeNewlines(JSON.stringify(v)).replace(/\\r\\n/g, '\\n');
    } catch {
      return String(v);
    }
  }
  // NOT the escaped fold — a scalar's real CRLF is already gone, and a C# source may legitimately
  // contain the two-character text \r\n inside a string literal.
  return normalizeNewlines(String(v));
}

/** The key-building form of a value: trimmed + lowercased, so casing/padding is not an identity. */
function keyPart(v) {
  return normalizeValue(v).trim().toLowerCase();
}

/** Only real records survive — a null / string / array element is not a formula. */
function rowsOf(container, name) {
  const value = getProp(container, name);
  if (!Array.isArray(value)) return [];
  return value.filter((r) => r && typeof r === 'object' && !Array.isArray(r));
}

/**
 * `payloadJson` → `{ functions }`. Accepts the raw string off the API, an already-parsed object,
 * null/'' , or complete garbage — a broken payload yields an empty diff, never a throw, because this
 * runs inside a dialog the admin opened to LOOK at history.
 */
export function parseSnapshotPayload(payloadJson) {
  let raw = payloadJson;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return EMPTY_PAYLOAD();
    try {
      raw = JSON.parse(trimmed);
    } catch {
      return EMPTY_PAYLOAD();
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return EMPTY_PAYLOAD();
  return { functions: rowsOf(raw, 'functions') };
}

/** Natural key of a formula: its FunctionCode — the C# class name, unique per calc version. */
export function snapshotFunctionKey(fn) {
  return keyPart(getProp(fn, 'functionCode'));
}

/**
 * Display form of a value, cut at `max` with an ellipsis — for `sourceCode` and the JSON blobs,
 * which are compared whole but must not be rendered whole.
 */
export function truncateValue(v, max = 300) {
  const s = normalizeValue(v);
  if (!(max > 0)) return '';
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/**
 * Union of the comparable keys of both rows, as `lowercase → the casing to display`.
 * `first` is consulted first so the current (right) side decides the casing and the field order.
 */
function comparableKeys(first, second) {
  const seen = new Map();
  [first, second].forEach((row) => {
    if (!row) return;
    Object.keys(row).forEach((k) => {
      const lk = k.toLowerCase();
      if (IGNORED.has(lk) || seen.has(lk)) return;
      seen.set(lk, k);
    });
  });
  return seen;
}

/** Only the fields that actually differ — an unchanged formula therefore reports `fields: []`. */
function changedFields(left, right) {
  const out = [];
  comparableKeys(right, left).forEach((display, lk) => {
    const lv = normalizeValue(getProp(left, lk));
    const rv = normalizeValue(getProp(right, lk));
    if (lv !== rv) out.push({ name: display, left: lv, right: rv });
  });
  return out;
}

/** For an added/removed formula: everything it carries, so the panel can show what appeared/vanished. */
function presentFields(row, side) {
  const out = [];
  comparableKeys(row, null).forEach((display, lk) => {
    const v = normalizeValue(getProp(row, lk));
    if (v === '') return;
    out.push(side === 'right' ? { name: display, left: '', right: v } : { name: display, left: v, right: '' });
  });
  return out;
}

/**
 * Index rows by natural key, keeping insertion order. A repeated key gets `#1`, `#2`, … appended
 * (the FIRST occurrence keeps the bare key, so one-on-one still matches when only one side
 * duplicates). `FunctionCode` has no unique index in the database, and a hand-written payload can
 * carry two blank codes — without this they would collapse into one another and silently disappear.
 */
function indexByKey(rows, keyFn) {
  const map = new Map();
  const order = [];
  (rows || []).forEach((row) => {
    const base = keyFn(row);
    let key = base;
    let n = 0;
    while (map.has(key)) {
      n += 1;
      key = `${base}#${n}`;
    }
    map.set(key, row);
    order.push(key);
  });
  return { map, order };
}

function functionMeta(row) {
  const functionCode = normalizeValue(getProp(row, 'functionCode'));
  return {
    label: normalizeValue(getProp(row, 'name')) || functionCode,
    functionCode,
    fieldKey: normalizeValue(getProp(row, 'fieldKey')),
  };
}

/** Right (current) order first, then the rows that only the left (older) side had. */
function diffRows(leftRows, rightRows, keyFn, metaFn) {
  const L = indexByKey(leftRows, keyFn);
  const R = indexByKey(rightRows, keyFn);
  const out = [];

  R.order.forEach((key) => {
    const right = R.map.get(key);
    if (!L.map.has(key)) {
      out.push({ key, state: 'added', ...metaFn(right), fields: presentFields(right, 'right') });
      return;
    }
    const fields = changedFields(L.map.get(key), right);
    out.push({ key, state: fields.length ? 'modified' : 'unchanged', ...metaFn(right), fields });
  });

  L.order.forEach((key) => {
    if (R.map.has(key)) return;
    const left = L.map.get(key);
    out.push({ key, state: 'removed', ...metaFn(left), fields: presentFields(left, 'left') });
  });

  return out;
}

/**
 * Compare two snapshot payloads (older on the left, newer on the right).
 *
 * → `{ summary: { added, removed, modified, unchanged },
 *      functions: [ { key, state, label, functionCode, fieldKey, fields } ] }`
 * where `fields` is `[ { name, left, right } ]` and is `[]` on an unchanged formula.
 */
export function diffSnapshots(leftPayloadJson, rightPayloadJson) {
  const left = parseSnapshotPayload(leftPayloadJson);
  const right = parseSnapshotPayload(rightPayloadJson);

  const functions = diffRows(left.functions, right.functions, snapshotFunctionKey, functionMeta);

  const summary = { added: 0, removed: 0, modified: 0, unchanged: 0 };
  functions.forEach((row) => {
    summary[row.state] += 1;
  });

  return { summary, functions };
}
